import React from 'react';
import { formatDate as formatDateUtil } from '../../utils/dateUtils';

/**
 * AdminMarriageDeclarationPreview - Read-only admin preview for Marriage Declaration form
 * 
 * Renders Marriage Declaration form data directly from submission.form_data
 * No routing, no API calls, no submit functionality.
 * Designed for Employee360 modal display.
 */
export default function AdminMarriageDeclarationPreview({ submission, readOnly = true }) {
  const formData = submission?.form_data || {};

  const formatDate = (dateStr) => {
    return formatDateUtil(dateStr, '________________________');
  };

  return (
    <div className="bg-gray-50 py-6 px-4">
      <div className="max-w-4xl mx-auto px-6">
        {/* Government Form Preview */}
        <div className="bg-white border-2 border-gray-800 p-8 shadow-lg">
          {/* Form Title */}
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              DECISION NO. 2 UNDER RULE 21 OF CENTRAL CIVIL SERVICES (CONDUCT) RULES, 1964
            </h2>
            <p className="text-gray-700">Marriage Declaration Form</p>
          </div>

          {/* Employee Details Section */}
          <div className="mb-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-semibold text-gray-800">Name of the Government Servant :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formData.employeeName || '________________________'}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Employee Code :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formData.employeeCode || '________________________'}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Department :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formData.department || '________________________'}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Designation :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formData.designation || '________________________'}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Date of Birth :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formatDate(formData.dateOfBirth)}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Date of Appointment :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formatDate(formData.dateOfAppointment)}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Date of Retirement :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formatDate(formData.dateOfRetirement)}</span>
                </div>
              </div>
              <div className="col-span-2">
                <span className="font-semibold text-gray-800">Permanent Address :</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900 whitespace-pre-line">{formData.permanentAddress || '________________________'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Marital Status Section */}
          <div className="mb-6">
            <h3 className="font-bold text-gray-900 mb-4">Marital Status Declaration</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-gray-800">I declare that I am:</span>
              </div>
              <div className="pl-4 space-y-2">
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 border border-gray-800 inline-block"></span>
                  <span className="text-gray-900">Unmarried</span>
                  {formData.maritalStatus === 'unmarried' && <span className="text-gray-900 font-bold">✓</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 border border-gray-800 inline-block"></span>
                  <span className="text-gray-900">Widower</span>
                  {formData.maritalStatus === 'widower' && <span className="text-gray-900 font-bold">✓</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 border border-gray-800 inline-block"></span>
                  <span className="text-gray-900">Widow</span>
                  {formData.maritalStatus === 'widow' && <span className="text-gray-900 font-bold">✓</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 border border-gray-800 inline-block"></span>
                  <span className="text-gray-900">Married and only one spouse living</span>
                  {formData.maritalStatus === 'married_one_spouse' && <span className="text-gray-900 font-bold">✓</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-4 h-4 border border-gray-800 inline-block"></span>
                  <span className="text-gray-900">Married while spouse living</span>
                  {formData.maritalStatus === 'married_while_spouse_living' && <span className="text-gray-900 font-bold">✓</span>}
                </div>
              </div>

              {formData.maritalStatus === 'married_while_spouse_living' && (
                <div className="mt-4 pl-4">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-800">Application for exemption enclosed:</span>
                    <span className="font-semibold text-gray-900">{formData.exemptionEnclosed === 'yes' ? 'Yes' : 'No'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Declaration Section */}
          <div className="mb-6">
            <div className="text-sm text-gray-800 mb-4">
              <p className="mb-2">
                I hereby declare that the information provided above is true and correct to the best of my knowledge and belief.
              </p>
              <p>
                I understand that any false statement may lead to disciplinary action under the Central Civil Services (Conduct) Rules, 1964.
              </p>
            </div>
          </div>

          {/* Signature Section */}
          <div className="mb-6">
            <div className="grid grid-cols-2 gap-8 text-sm">
              <div>
                <span className="font-semibold text-gray-800">Place:</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formData.place || '________________________'}</span>
                </div>
              </div>
              <div>
                <span className="font-semibold text-gray-800">Date:</span>
                <div className="mt-1 border-b border-gray-800 pb-1">
                  <span className="text-gray-900">{formatDate(formData.declarationDate)}</span>
                </div>
              </div>
            </div>
            <div className="mt-8">
              <div className="border-b border-gray-800 pb-1 w-64">
                <span className="text-gray-900">Signature of Government Servant</span>
              </div>
            </div>
          </div>

          {/* Note Section */}
          <div className="mt-8 pt-4 border-t border-gray-300">
            <p className="text-xs text-gray-600 italic">
              Note: This declaration is required under Rule 21 of Central Civil Services (Conduct) Rules, 1964. 
              Any false declaration may result in disciplinary action.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
